/**
 * InputRow — Input zone of the unified Activity Bar surface.
 *
 * Always-visible input area at the bottom of the Activity Bar:
 *   - AI preset chips (above input, when AI enabled)
 *   - Text input with inline send button (flex: 1)
 *   - Doc type dropdown — persistent, filters by context
 *
 * Phase 6: Search mode — on /patients route, InputRow filters the patient list.
 * Phase 7: AI preset chips moved here from ChatMessages. Dynamic placeholder
 * based on page context.
 *
 * See: docs/features/infrastructure/app-shell.md
 */
import React, { useState, useRef, forwardRef, useImperativeHandle, useEffect, useMemo } from "react";
import { Send, X, User, Share2 } from "lucide-react";
import Tooltip from "@mui/material/Tooltip";
import Popper from "@mui/material/Popper";
import { useNavigate } from "react-router-dom";
import { matchPatients } from "../../hooks/usePatientNameIndex";

// Patient quick search: minimum typed characters before the dropdown appears
const QUICK_SEARCH_MIN_CHARS = 3;

/**
 * @param {Object} props
 * @param {Function} props.onSend - Handler called with message text
 * @param {boolean} props.disabled - Disable input (e.g. offline)
 * @param {Object} props.dropdownState - Doc type dropdown state
 * @param {Function} props.onFileDrop - Handler for dropped files
 * @param {Function} props.onPasteDetected - Handler for detected long paste
 * @param {string} props.placeholder - Input placeholder text
 * @param {boolean} props.searchMode - When true, input filters instead of sending
 * @param {Function} props.onSearchChange - Called with search text in search mode
 * @param {Array} props.chatPresets - AI preset chips from actions.json
 * @param {Function} props.onPresetClick - Handler for preset chip clicks
 * @param {boolean} props.aiEnabled - Whether AI chat is enabled
 * @param {Function|null} props.onClearChat - Handler to clear chat history (null when no messages)
 * @param {Array} props.quickSearchPatients - Patient name index for quick search (empty disables)
 * @param {string} props.quickSearchDirection - Dropdown opens "up" (default) or "down" (inline mode)
 */
const InputRow = forwardRef(({
    onSend,
    disabled = false,
    dropdownState,
    onFileDrop,
    onPasteDetected,
    placeholder = "Type a message\u2026",
    searchMode = false,
    onSearchChange,
    chatPresets = [],
    onPresetClick,
    aiEnabled = false,
    onClearChat = null,
    quickSearchPatients = [],
    quickSearchDirection = "up",
}, ref) => {
    const [inputValue, setInputValue] = useState("");
    const [isDragOver, setIsDragOver] = useState(false);
    // Patient quick search dropdown state
    const [quickSearchDismissed, setQuickSearchDismissed] = useState(false);
    const [highlightIndex, setHighlightIndex] = useState(-1);
    const inputRef = useRef(null);
    // Anchor for the quick-search Popper (portal escapes the bar's overflow:hidden)
    const inputContainerRef = useRef(null);
    const navigate = useNavigate();

    // Patient quick search matches \u2014 only in chat mode with an index available
    const quickSearchMatches = useMemo(() => {
        if (searchMode || quickSearchDismissed || quickSearchPatients.length === 0) return [];
        if (inputValue.trim().length < QUICK_SEARCH_MIN_CHARS) return [];
        return matchPatients(quickSearchPatients, inputValue);
    }, [inputValue, searchMode, quickSearchDismissed, quickSearchPatients]);

    const quickSearchOpen = quickSearchMatches.length > 0;

    // New keystroke: clear dismissal and reset keyboard highlight
    const handleInputChange = (e) => {
        setInputValue(e.target.value);
        setQuickSearchDismissed(false);
        setHighlightIndex(-1);
    };

    const selectQuickSearchPatient = (patient) => {
        setInputValue("");
        setQuickSearchDismissed(false);
        setHighlightIndex(-1);
        navigate("/patient", { state: { patientId: patient.id } });
    };

    // Expose focus() to parent via ref
    useImperativeHandle(ref, () => ({
        focus: () => inputRef.current?.focus(),
        clear: () => { setInputValue(""); onSearchChange?.(""); },
    }));

    // Long paste threshold (chars)
    const PASTE_THRESHOLD = 500;

    // In search mode, emit changes to parent for real-time filtering
    useEffect(() => {
        if (searchMode) {
            onSearchChange?.(inputValue);
        }
    }, [inputValue, searchMode]); // eslint-disable-line react-hooks/exhaustive-deps

    // Clear search when exiting search mode
    useEffect(() => {
        if (!searchMode && inputValue) {
            setInputValue("");
        }
    }, [searchMode]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSubmit = (e) => {
        e?.preventDefault();
        if (searchMode) return; // No submit in search mode
        const trimmed = inputValue.trim();
        if (!trimmed || disabled) return;
        onSend?.(trimmed);
        setInputValue("");
    };

    const handleKeyDown = (e) => {
        if (searchMode) return; // No Enter-to-submit in search mode

        // Patient quick search keyboard navigation
        if (quickSearchOpen) {
            if (e.key === "ArrowDown") {
                e.preventDefault();
                setHighlightIndex((i) => (i + 1) % quickSearchMatches.length);
                return;
            }
            if (e.key === "ArrowUp") {
                e.preventDefault();
                setHighlightIndex((i) => (i <= 0 ? quickSearchMatches.length - 1 : i - 1));
                return;
            }
            if (e.key === "Escape") {
                e.preventDefault();
                setQuickSearchDismissed(true);
                setHighlightIndex(-1);
                return;
            }
            // Enter on a highlighted row selects the patient; Enter with no
            // highlight falls through to normal chat send.
            if (e.key === "Enter" && !e.shiftKey && highlightIndex >= 0) {
                e.preventDefault();
                selectQuickSearchPatient(quickSearchMatches[highlightIndex]);
                return;
            }
        }

        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSubmit();
        }
    };

    const handlePaste = (e) => {
        const text = e.clipboardData?.getData("text/plain") || "";
        if (text.length > PASTE_THRESHOLD && onPasteDetected) {
            e.preventDefault();
            onPasteDetected(text);
        }
    };

    // Drag-and-drop handlers
    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragOver(true);
    };

    const handleDragLeave = () => {
        setIsDragOver(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragOver(false);
        const files = Array.from(e.dataTransfer?.files || []);
        if (files.length > 0 && onFileDrop) {
            onFileDrop(files);
        }
    };

    const hasContent = inputValue.trim().length > 0;

    const effectivePlaceholder = searchMode ? "Search patients\u2026" : placeholder;

    const handleClearSearch = () => {
        setInputValue("");
        onSearchChange?.("");
        inputRef.current?.focus();
    };

    return (
        <div
            className={`input-row${isDragOver ? " input-row-drag-over" : ""}${searchMode ? " input-row-search-mode" : ""}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
        >
            {/* Text input with inline AI chips + send/clear button */}
            <div className="input-row-input-container" ref={inputContainerRef}>
                <input
                    ref={inputRef}
                    className="input-row-text userback-ignore"
                    type="text"
                    value={inputValue}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    onPaste={searchMode ? undefined : handlePaste}
                    onBlur={() => setQuickSearchDismissed(true)}
                    placeholder={effectivePlaceholder}
                    disabled={disabled}
                    aria-label={searchMode ? "Search patients" : "Chat input"}
                    role="combobox"
                    aria-expanded={quickSearchOpen}
                    aria-controls="patient-quick-search-listbox"
                    aria-autocomplete="list"
                />
                {/* Patient quick search dropdown — appears after 3 typed characters.
                    Rendered in a Popper portal so the Activity Bar's overflow:hidden
                    can't clip it (the inline dashboard bar is only ~260px tall). */}
                <Popper
                    open={quickSearchOpen}
                    anchorEl={inputContainerRef.current}
                    placement={quickSearchDirection === "down" ? "bottom-start" : "top-start"}
                    style={{ zIndex: 2000 }}
                    modifiers={[{ name: "offset", options: { offset: [0, 4] } }]}
                >
                    <div
                        id="patient-quick-search-listbox"
                        className="quick-search-dropdown"
                        style={{ width: inputContainerRef.current?.offsetWidth || undefined }}
                        role="listbox"
                        aria-label="Patient quick search results"
                    >
                        <div className="quick-search-heading">Go to patient</div>
                        {quickSearchMatches.map((patient, idx) => (
                            <button
                                key={patient.id}
                                className={`quick-search-item${idx === highlightIndex ? " quick-search-item-highlighted" : ""}`}
                                role="option"
                                aria-selected={idx === highlightIndex}
                                // onMouseDown (not onClick) so selection beats the input's blur
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    selectQuickSearchPatient(patient);
                                }}
                                onMouseEnter={() => setHighlightIndex(idx)}
                            >
                                <User size={12} className="quick-search-item-icon" />
                                <span className="quick-search-item-name">
                                    {patient.firstName} {patient.lastName}
                                </span>
                                {patient.isShared && (
                                    <span className="quick-search-item-shared">
                                        <Share2 size={10} />
                                        Shared
                                    </span>
                                )}
                            </button>
                        ))}
                    </div>
                </Popper>
                {/* AI preset chips — flush right, left of send btn, hidden when typing */}
                {!searchMode && aiEnabled && chatPresets.length > 0 && !hasContent && (
                    <div className="input-row-inline-chips">
                        {chatPresets.map((preset) => (
                            <button
                                key={preset.id}
                                className="input-row-inline-chip"
                                onClick={() => onPresetClick?.(preset)}
                                disabled={disabled}
                            >
                                {preset.label}
                            </button>
                        ))}
                        {onClearChat && (
                            <button
                                className="input-row-inline-chip input-row-inline-chip-clear"
                                onClick={onClearChat}
                            >
                                Clear
                            </button>
                        )}
                    </div>
                )}
                {searchMode ? (
                    /* Clear button in search mode */
                    hasContent && (
                        <Tooltip title="Clear search" arrow>
                            <button
                                className="input-row-btn-send input-row-btn-send-visible input-row-btn-clear"
                                onClick={handleClearSearch}
                                aria-label="Clear search"
                            >
                                <X size={16} />
                            </button>
                        </Tooltip>
                    )
                ) : (
                    /* Send button in chat mode */
                    <Tooltip title="Send" arrow>
                        <button
                            className={`input-row-btn-send${hasContent ? " input-row-btn-send-visible" : ""}`}
                            onClick={handleSubmit}
                            disabled={disabled || !hasContent}
                            aria-label="Send message"
                        >
                            <Send size={16} />
                        </button>
                    </Tooltip>
                )}
            </div>

            {/* Doc type dropdown — hidden in search mode */}
            {!searchMode && dropdownState?.options?.length > 0 && (
                <select
                    className="input-row-doc-type"
                    value={dropdownState.selectedType || ""}
                    onChange={(e) => dropdownState.setSelectedType?.(e.target.value)}
                    aria-label="Document type"
                >
                    {dropdownState.options.map((opt) => (
                        <option key={opt.value || opt.id} value={opt.value || opt.id}>
                            {opt.label || opt.name}
                        </option>
                    ))}
                </select>
            )}
        </div>
    );
});

InputRow.displayName = "InputRow";

export default InputRow;
